import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { ExternalLink, ClipboardList } from "lucide-react";
import api, { errMsg } from "@/lib/api";
import AdminShell from "@/components/AdminShell";
import { PROFILE_STATUS_STYLES } from "@/lib/constants";

export default function AdminQueue() {
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(true);

    const load = async () => {
        try {
            const { data } = await api.get("/admin/queue");
            setItems(data || []);
        } catch (err) {
            toast.error(errMsg(err, "Failed to load queue"));
        } finally {
            setLoading(false);
        }
    };
    useEffect(() => {
        load();
    }, []);

    return (
        <AdminShell>
            <div className="animate-fade-up">
                <div className="text-xs uppercase tracking-widest text-[#0F766E] font-semibold">
                    Approval queue
                </div>
                <h1 className="mt-1 text-3xl font-bold text-[#123B5D]">
                    Profiles awaiting review
                </h1>
                <p className="mt-1 text-sm text-[#17212B]/60">
                    Split view opens on the doctor&apos;s row.
                </p>

                <div className="mt-8 ddi-card overflow-hidden" data-testid="admin-queue-table">
                    <table className="w-full text-sm">
                        <thead className="bg-[#123B5D]/5 text-[#123B5D]">
                            <tr>
                                <th className="text-left px-4 py-3 font-semibold">Doctor</th>
                                <th className="text-left px-4 py-3 font-semibold">Specialization</th>
                                <th className="text-left px-4 py-3 font-semibold">Package</th>
                                <th className="text-left px-4 py-3 font-semibold">Payment</th>
                                <th className="text-left px-4 py-3 font-semibold">Status</th>
                                <th className="text-left px-4 py-3 font-semibold">Submitted</th>
                                <th className="px-4 py-3" />
                            </tr>
                        </thead>
                        <tbody>
                            {loading ? (
                                <tr>
                                    <td colSpan={7} className="p-10 text-center text-[#17212B]/40">
                                        Loading…
                                    </td>
                                </tr>
                            ) : items.length === 0 ? (
                                <tr>
                                    <td colSpan={7} className="p-10 text-center text-[#17212B]/50">
                                        <ClipboardList size={24} className="inline mb-2 text-[#17212B]/30" />
                                        <div>Queue is empty.</div>
                                    </td>
                                </tr>
                            ) : (
                                items.map((it) => {
                                    const st = PROFILE_STATUS_STYLES[it.profile_status];
                                    return (
                                        <tr
                                            key={it.doctor_id}
                                            className="border-t border-[#123B5D]/5 hover:bg-[#F5F8FA]/50"
                                            data-testid={`queue-row-${it.doctor_id}`}
                                        >
                                            <td className="px-4 py-3 font-medium text-[#123B5D]">
                                                {it.full_name}
                                            </td>
                                            <td className="px-4 py-3 text-[#17212B]/80">{it.specialization || "—"}</td>
                                            <td className="px-4 py-3 text-[#17212B]/80 capitalize">{it.package_code || "—"}</td>
                                            <td className="px-4 py-3 text-[#17212B]/70 capitalize">
                                                {(it.payment_state || "unpaid").replaceAll("_", " ")}
                                            </td>
                                            <td className="px-4 py-3">
                                                <span className={"ddi-badge " + (st?.cls || "")}>
                                                    {st?.label || it.profile_status}
                                                </span>
                                            </td>
                                            <td className="px-4 py-3 text-[#17212B]/60 text-xs">
                                                {it.submitted_at ? new Date(it.submitted_at).toLocaleString() : "—"}
                                            </td>
                                            <td className="px-4 py-3 text-right">
                                                <Link
                                                    to={`/admin/review/${it.doctor_id}`}
                                                    className="inline-flex items-center gap-1 text-[#0F766E] font-semibold text-xs hover:underline"
                                                    data-testid={`queue-open-${it.doctor_id}`}
                                                >
                                                    Open review <ExternalLink size={12} />
                                                </Link>
                                            </td>
                                        </tr>
                                    );
                                })
                            )}
                        </tbody>
                    </table>
                </div>
            </div>
        </AdminShell>
    );
}
